import { add, fromPolar, lerp, negate, scale, segment, sub, vec, type Vec2 } from '../geom';
import { formatLength, ROUND_TEXTS, type RoundDimensionNode } from '../model';
import { arrowhead, gapOf, joined, leaderText, textAlong, textWidth } from './parts';
import type { DimensionDefinition, DimensionLook } from './types';

/**
 * Whether text and `arrows` arrowheads fit along `room` of the dimension line inside the
 * circle, with a gap either side of the text.
 */
const fits = (room: number, content: string, arrows: number, look: DimensionLook): boolean =>
  textWidth(content, look) + arrows * look.arrowSize + 2 * gapOf(look) <= room;

/**
 * Where text too long for the circle goes: on a leader running on from `rim` along
 * `direction`, past the arrow drawn there.
 */
const outside = (
  rim: Vec2,
  direction: Vec2,
  degrees: number,
  content: string,
  look: DimensionLook,
) => leaderText(rim, direction, degrees, look.arrowSize + gapOf(look), content, look);

const fromRecord: DimensionDefinition<RoundDimensionNode>['fromRecord'] = (
  [x, y, radius, angle, text],
  kind,
) => ({
  kind,
  center: vec(x!, y!),
  radius: radius!,
  angle: angle!,
  text: ROUND_TEXTS[text! - 1] ?? null,
});

/** Whether the text goes inside: as the script says, or when it fits. */
const inside = (
  node: RoundDimensionNode,
  room: number,
  content: string,
  arrows: number,
  look: DimensionLook,
) => (node.text === null ? fits(room, content, arrows, look) : node.text === 'inside');

/**
 * A circle's radius: a line from its center out to it at `angle`, ending in an arrow, or,
 * when its text goes outside, an arrow on the rim pointing in and a leader past it.
 */
export const RADIUS: DimensionDefinition<RoundDimensionNode> = {
  label: 'Radius dimension',
  arity: 5,
  fromRecord,
  resolve(node, look) {
    const direction = fromPolar(vec(0, 0), 1, node.angle);
    const rim = add(node.center, scale(direction, node.radius));
    const content = `R${formatLength(node.radius, look.display)}`;
    if (inside(node, node.radius, content, 1, look)) {
      const end = arrowhead(rim, direction, look);
      return {
        value: node.radius,
        layout: {
          strokes: [segment(node.center, rim), ...end.strokes],
          fills: end.fills,
          text: textAlong(lerp(node.center, rim, 0.5), node.angle, content, look),
        },
      };
    }
    // Too small: the arrow sits on the rim pointing in, and the text rides a leader outside.
    const end = arrowhead(rim, negate(direction), look);
    const leader = outside(rim, direction, node.angle, content, look);
    return {
      value: node.radius,
      layout: {
        strokes: [segment(rim, leader.end), ...end.strokes],
        fills: end.fills,
        text: leader.text,
      },
    };
  },
};

/** A circle's diameter: a line across it through its center at `angle`, arrows at both ends. */
export const DIAMETER: DimensionDefinition<RoundDimensionNode> = {
  label: 'Diameter dimension',
  arity: 5,
  fromRecord,
  resolve(node, look) {
    const direction = fromPolar(vec(0, 0), 1, node.angle);
    const near = sub(node.center, scale(direction, node.radius));
    const far = add(node.center, scale(direction, node.radius));
    const value = node.radius * 2;
    const content = `Ø${formatLength(value, look.display)}`;
    if (inside(node, value, content, 2, look)) {
      const ends = joined([
        arrowhead(near, negate(direction), look),
        arrowhead(far, direction, look),
      ]);
      return {
        value,
        layout: {
          strokes: [segment(near, far), ...ends.strokes],
          fills: ends.fills,
          text: textAlong(node.center, node.angle, content, look),
        },
      };
    }
    // Too small: both arrows sit on the rim pointing in, and the text rides a leader past
    // the far side.
    const ends = joined([
      arrowhead(near, direction, look),
      arrowhead(far, negate(direction), look),
    ]);
    const leader = outside(far, direction, node.angle, content, look);
    const tail = sub(near, scale(direction, look.arrowSize));
    return {
      value,
      layout: {
        strokes: [segment(tail, leader.end), ...ends.strokes],
        fills: ends.fills,
        text: leader.text,
      },
    };
  },
};
